'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { Plus, PenSquare } from 'lucide-react';
import { BotIcon as Bot } from '@/components/icons/bot-icon';
import { ChatIcon } from '@/components/icons/chat-icon';
import { useAuth } from '@/components/auth-provider';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import { useTranslation } from '@/components/i18n-provider';
import { useAnalytics } from '@/hooks/use-analytics';
import { haptic } from '@/lib/haptic';
import { cn } from '@/lib/utils';
import { PopoverPortal, useAnchorRect } from '@/components/ui/popover-portal';

export type CreateMenuItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick?: () => void;
  href?: string;
};

export type CreateMenuProps = {
  className?: string;
  /** Where the menu opens relative to the trigger. */
  placement?: 'below' | 'above';
  /**
   * Horizontal alignment of the dropdown relative to the trigger.
   * Defaults to "stretch" (legacy behavior — menu grows to trigger width).
   * Use "end" for icon-button triggers so the menu has room to open.
   */
  align?: 'stretch' | 'start' | 'end';
  /** Override the trigger button (defaults to a primary "Create" pill). */
  trigger?: (args: { open: boolean; toggle: () => void }) => React.ReactNode;
  /** Extra items appended after the default agent/post/channel/chat items. */
  extraItems?: CreateMenuItem[];
  /**
   * Default item ids to hide from the menu. Use to drop entries that
   * have moved elsewhere — e.g. the workspace's "+" FAB hides
   * `post`/`channel` because those flows now live in the Activity /
   * Channels tabs of the bottom panel.
   */
  hiddenDefaults?: Array<'agent' | 'post' | 'channel' | 'chat'>;
  /** Track id namespace for analytics. */
  analyticsNamespace?: string;
  /**
   * Render the dropdown body via React Portal at `document.body` with
   * `position: fixed`. Use when the trigger lives inside a stacking
   * context that would otherwise trap the menu beneath higher layers
   * (e.g. workspace edge panels at z-[90]).
   */
  portal?: boolean;
};

export function CreateMenu({
  className,
  placement = 'below',
  align = 'stretch',
  trigger,
  extraItems = [],
  hiddenDefaults = [],
  analyticsNamespace = 'create-menu',
  portal = false,
}: CreateMenuProps) {
  const { user, loading: authLoading } = useAuth();
  const chatSidebar = useChatSidebar();
  const { t } = useTranslation();
  const { trackNavClick } = useAnalytics();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const portalBodyRef = useRef<HTMLDivElement>(null);
  const anchorRect = useAnchorRect(portal && open, wrapperRef);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      const insideTrigger = wrapperRef.current?.contains(target);
      const insidePortal = portalBodyRef.current?.contains(target);
      if (!insideTrigger && !insidePortal) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // URL_ACTIONS `?menu=create` (and the user-menu version) dispatch a
  // `workspace:open-menu` event so a shareable link can pop the menu
  // open without bouncing into a separate route. We respond when the
  // detail's `id` matches our analyticsNamespace prefix — the
  // workspace FAB uses `workspace-create-fab`, the header user menu
  // uses `user-menu`, etc.
  useEffect(() => {
    const onOpenMenu = (e: Event) => {
      const ev = e as CustomEvent<{ id?: string }>;
      const id = ev.detail?.id;
      if (!id) return;
      const matchesCreate =
        (id === 'create' && analyticsNamespace.includes('create')) ||
        (id === 'user' && analyticsNamespace.includes('user'));
      if (!matchesCreate) return;
      setOpen(true);
    };
    window.addEventListener('workspace:open-menu', onOpenMenu);
    return () => window.removeEventListener('workspace:open-menu', onOpenMenu);
  }, [analyticsNamespace]);

  const close = () => setOpen(false);
  const toggle = () => {
    haptic('light');
    setOpen((prev) => !prev);
  };

  const defaultTrigger = (
    <button
      onClick={() => {
        trackNavClick(`${analyticsNamespace}-toggle`);
        toggle();
      }}
      className={cn(
        'flex items-center justify-center gap-2 rounded-lg pl-4 pr-6 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer active:scale-[0.97]',
        open
          ? 'bg-primary/20 text-primary dark:bg-primary/25'
          : 'bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md hover:shadow-primary/20',
      )}
    >
      <Plus
        className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-45')}
      />
      {t('sidebar.create')}
    </button>
  );

  const guestTrigger = (
    <Link
      href="/login?redirect=/create"
      onClick={() => trackNavClick(`${analyticsNamespace}-guest`)}
      className="inline-flex items-center gap-2 rounded-lg pl-4 pr-6 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer active:scale-[0.97] bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md hover:shadow-primary/20"
    >
      <Plus className="h-4 w-4" />
      {t('sidebar.createAgentGuest')}
    </Link>
  );

  // Only the legacy default/guest trigger pills need to wait for auth
  // (their copy/route differ for guests). When the caller supplies a
  // custom `trigger` — the workspace `CreateFab` "+" puck — render it
  // immediately so it paints in lockstep with the sibling FABs instead
  // of popping in after the `/api/auth/session` round-trip. Menu item
  // clicks still route through the signup gate when anon.
  if (authLoading && !trigger) {
    return null;
  }

  // Guest path: the legacy `guestTrigger` (a "Create Agent" pill
  // routing to `/login?redirect=/create`) is only used when the
  // caller didn't supply a custom trigger. Callers that DO pass a
  // trigger — e.g. the workspace `CreateFab` — want the SAME chrome
  // shape across authed and anon modes so the layout doesn't shift
  // when the visitor logs in. In that path we still open the menu;
  // selecting an item routes through the signup gate (server-side
  // 401 → SignupGateProvider) since every action behind it mutates.
  if (!user && !trigger) {
    return <div className={cn('relative', className)}>{guestTrigger}</div>;
  }

  // Body content reused for both inline and portal render paths.
  const hide = (id: 'agent' | 'post' | 'channel' | 'chat') =>
    hiddenDefaults.includes(id);
  const anyDefaultVisible =
    !hide('agent') || !hide('post') || !hide('channel') || !hide('chat');
  const menuItems = (
    <>
          {!hide('agent') && (
            <Link
              role="menuitem"
              href="/create?tab=agent"
              onClick={() => {
                trackNavClick(`${analyticsNamespace}-agent`);
                haptic('selection');
                close();
              }}
              className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <Bot className="h-4 w-4 text-muted-foreground" />
              {t('sidebar.createAgent')}
            </Link>
          )}
          {!hide('post') && (
            <Link
              role="menuitem"
              href="/create?tab=post"
              onClick={() => {
                trackNavClick(`${analyticsNamespace}-post`);
                haptic('selection');
                close();
              }}
              className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <PenSquare className="h-4 w-4 text-muted-foreground" />
              {t('sidebar.createPost')}
            </Link>
          )}
          {!hide('channel') && (
            <Link
              role="menuitem"
              href="/create?tab=channel"
              onClick={() => {
                trackNavClick(`${analyticsNamespace}-channel`);
                haptic('selection');
                close();
              }}
              className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors"
            >
              <span className="h-4 w-4 flex items-center justify-center font-mono text-xs font-bold text-muted-foreground">
                //
              </span>
              {t('sidebar.createChannel')}
            </Link>
          )}
          {!hide('chat') && (
            <button
              role="menuitem"
              onClick={() => {
                trackNavClick(`${analyticsNamespace}-chat`);
                haptic('selection');
                close();
                chatSidebar.openNewChat();
              }}
              className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors w-full text-left"
            >
              <ChatIcon className="h-4 w-4 text-muted-foreground" eyes={false} />
              {t('sidebar.createChat')}
            </button>
          )}
          {anyDefaultVisible && extraItems.length > 0 && (
            <div className="my-1 h-px bg-border/60" />
          )}
          {extraItems.map((item) => {
            const handleClick = () => {
              trackNavClick(`${analyticsNamespace}-${item.id}`);
              haptic('selection');
              close();
              item.onClick?.();
            };
            if (item.href) {
              return (
                <Link
                  key={item.id}
                  role="menuitem"
                  href={item.href}
                  onClick={handleClick}
                  className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors"
                >
                  <span className="h-4 w-4 flex items-center justify-center text-muted-foreground">
                    {item.icon}
                  </span>
                  {item.label}
                </Link>
              );
            }
            return (
              <button
                key={item.id}
                role="menuitem"
                onClick={handleClick}
                className="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-accent transition-colors w-full text-left"
              >
                <span className="h-4 w-4 flex items-center justify-center text-muted-foreground">
                  {item.icon}
                </span>
                {item.label}
              </button>
            );
          })}
    </>
  );

  // Inline body — anchored absolute relative to the trigger's wrapper.
  const inlineBody = open ? (
    <div
      role="menu"
      aria-label={t('actions.create')}
      className={cn(
        'absolute rounded-lg border border-border bg-popover shadow-lg z-50 py-1 min-w-[14rem]',
        placement === 'above' ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
        align === 'stretch' && 'left-0 right-0',
        align === 'start' && 'left-0',
        align === 'end' && 'right-0',
      )}
    >
      {menuItems}
    </div>
  ) : null;

  // Portal body — fixed positioning with viewport coords from the trigger
  // wrapper. Sits above workspace edge panels (z-[200] vs panels' z-[90]).
  // Falls back to no-render until the first measurement lands.
  const MIN_WIDTH = 224; // 14rem
  const portalBody = open && anchorRect ? (
    <PopoverPortal
      role="menu"
      ariaLabel={t('actions.create')}
      style={{
        position: 'fixed',
        top:
          placement === 'below'
            ? Math.round(anchorRect.bottom + 6)
            : undefined,
        bottom:
          placement === 'above'
            ? Math.round(window.innerHeight - anchorRect.top + 6)
            : undefined,
        left:
          align === 'end'
            ? Math.max(8, Math.round(anchorRect.right - MIN_WIDTH))
            : Math.max(
                8,
                Math.min(
                  Math.round(anchorRect.left),
                  window.innerWidth - MIN_WIDTH - 8,
                ),
              ),
        minWidth:
          align === 'stretch'
            ? Math.round(anchorRect.width)
            : MIN_WIDTH,
        zIndex: 200,
      }}
      className="rounded-lg border border-border bg-popover shadow-lg py-1"
    >
      <div ref={portalBodyRef}>{menuItems}</div>
    </PopoverPortal>
  ) : null;

  return (
    <div className={cn('relative', className)} ref={wrapperRef}>
      {trigger ? trigger({ open, toggle }) : defaultTrigger}
      {portal ? portalBody : inlineBody}
    </div>
  );
}
